import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import CollapsibleItinerarySelector from '../../components/CollapsibleItinerarySelector';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Plus,
  Truck,
  Phone,
  EnvelopeSimple,
  MapPin,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';
import api from '../../services/api';
import { toast } from 'sonner';
import InfoTip from '../../components/InfoTip';
import CompactDetails from '../../components/CompactDetails';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';
import {
  buildVendorPayload,
  createVendorAssignments,
  getItineraryOptionLabel,
  getItinerarySideLabel,
  getSelectedItineraryIds,
  normalizeText,
} from './vendorAssignmentUtils';

const categoryConfig = {
  caterer: { label: 'Caterer', color: 'bg-orange-100 text-orange-700' },
  decorator: { label: 'Decorator', color: 'bg-pink-100 text-pink-700' },
  photographer: { label: 'Photographer', color: 'bg-blue-100 text-blue-700' },
  videographer: { label: 'Videographer', color: 'bg-purple-100 text-purple-700' },
  makeup: { label: 'Makeup Artist', color: 'bg-rose-100 text-rose-700' },
  dj: { label: 'DJ/Music', color: 'bg-cyan-100 text-cyan-700' },
  florist: { label: 'Florist', color: 'bg-green-100 text-green-700' },
  venue: { label: 'Venue', color: 'bg-violet-100 text-violet-700' },
  transportation: { label: 'Transportation', color: 'bg-yellow-100 text-yellow-700' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-700' }
};

const tierConfig = {
  low: { label: 'Low', color: 'bg-emerald-100 text-emerald-700' },
  med: { label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  high: { label: 'High', color: 'bg-rose-100 text-rose-700' }
};

const defaultCategoryOrder = Object.keys(categoryConfig);
const MASTER_VENDOR_NEW_VALUE = '__new__';
const ALL_CITIES_VALUE = '__all_cities__';

function formatCategoryLabel(category) {
  if (categoryConfig[category]) return categoryConfig[category].label;

  return category
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getCategoryAppearance(category) {
  return categoryConfig[category] || { label: formatCategoryLabel(category), color: 'bg-slate-100 text-slate-700' };
}

function getTierLabel(value) {
  if (!value) return 'Not set';
  return tierConfig[value]?.label || formatCategoryLabel(value);
}

function getTierColor(value) {
  return tierConfig[value]?.color || 'bg-slate-100 text-slate-700';
}

function getItinerarySideAppearance(side) {
  if (side === 'bride') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (side === 'groom') return 'border-sky-200 bg-sky-50 text-sky-700';
  return 'border-wedding-sage/30 bg-wedding-sage/10 text-wedding-sage';
}

function getItineraryScheduleLabel(item) {
  const date = item?.date ? new Date(item.date) : null;
  const dateLabel = date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  return [dateLabel, item?.start_time].filter(Boolean).join(' | ');
}

function sortItineraryItems(items) {
  return [...(items || [])]
    .filter((item) => item?.id)
    .sort((first, second) => {
      const firstValue = `${first.date || ''} ${first.start_time || ''}`;
      const secondValue = `${second.date || ''} ${second.start_time || ''}`;
      return firstValue.localeCompare(secondValue);
    });
}

function createVendorForm(initial = {}) {
  return {
    id: '',
    master_vendor_id: '',
    itinerary_item_id: '',
    itinerary_item_ids: [],
    name: '',
    category: 'caterer',
    custom_category: '',
    city: '',
    contact_name: '',
    phone: '',
    email: '',
    cost_level: 'med',
    quality_level: 'med',
    notes: '',
    ...initial
  };
}

function vendorToForm(vendor, assignments = []) {
  const isDefaultCategory = defaultCategoryOrder.includes(vendor?.category);
  const itineraryItemIds = Array.from(new Set(
    (assignments.length > 0 ? assignments : [vendor])
      .map((assignment) => assignment?.itinerary_item_id)
      .filter(Boolean)
  ));
  return createVendorForm({
    id: vendor?.id || '',
    master_vendor_id: vendor?.master_vendor_id || '',
    itinerary_item_id: vendor?.itinerary_item_id || '',
    itinerary_item_ids: itineraryItemIds,
    name: vendor?.name || '',
    category: isDefaultCategory ? vendor.category : (vendor?.category || 'other'),
    custom_category: isDefaultCategory ? '' : (vendor?.category || ''),
    city: vendor?.city || '',
    contact_name: vendor?.contact_name || '',
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    cost_level: vendor?.cost_level || 'med',
    quality_level: vendor?.quality_level || 'med',
    notes: vendor?.notes || ''
  });
}

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        const location = Array.isArray(item?.loc) ? item.loc.filter(Boolean).join('.') : '';
        const message = item?.msg || item?.message || JSON.stringify(item);
        return location ? `${location}: ${message}` : message;
      })
      .filter(Boolean)
      .join(', ');
  }

  return fallback;
}

function ItineraryCheckboxList({ items, selectedIds, onChange, idPrefix = 'itinerary', defaultSelectAll = true }) {
  return (
    <CollapsibleItinerarySelector
      items={items}
      selectedIds={selectedIds}
      onChange={onChange}
      title="Itinerary Events"
      required
      idPrefix={idPrefix}
      defaultSelectAll={defaultSelectAll}
      renderPrimaryLabel={getItineraryOptionLabel}
      className="space-y-0"
    />
  );
}

function VendorForm({
  vendor,
  setVendor,
  onSubmit,
  isEdit,
  availableCategories,
  masterVendors = [],
  showMasterPicker = false,
  onMasterVendorSelect = null,
  itineraryItems = [],
  requireItinerary = false,
  submitLabel = null
}) {
  const selectedCustomCategory =
    vendor.category && !defaultCategoryOrder.includes(vendor.category) ? vendor.category : '';
  const categorySelectValue = selectedCustomCategory ? 'other' : (vendor.category || 'caterer');
  const masterSelectValue = vendor.master_vendor_id || MASTER_VENDOR_NEW_VALUE;

  const updateCategory = (value) => {
    if (value === 'other') {
      setVendor({
        ...vendor,
        category: selectedCustomCategory || 'other',
        custom_category: selectedCustomCategory || vendor.custom_category || ''
      });
      return;
    }

    setVendor({
      ...vendor,
      category: value,
      custom_category: ''
    });
  };

  const handleMasterVendorChange = (value) => {
    if (value === MASTER_VENDOR_NEW_VALUE) {
      onMasterVendorSelect?.(null);
      return;
    }

    const selected = masterVendors.find((item) => item.id === value);
    if (!selected) return;

    onMasterVendorSelect?.(selected);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {showMasterPicker && (
        <div className="space-y-2">
          <Label htmlFor="master_vendor_id">Copy from Master List</Label>
          <Select value={masterSelectValue} onValueChange={handleMasterVendorChange}>
            <SelectTrigger id="master_vendor_id" data-testid="master-vendor-select">
              <SelectValue placeholder="Start from a saved vendor or create a new one" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={MASTER_VENDOR_NEW_VALUE}>Add new vendor</SelectItem>
              {masterVendors.map((masterVendor) => (
                <SelectItem key={masterVendor.id} value={masterVendor.id}>
                  {masterVendor.name}
                  {masterVendor.city ? ` (${masterVendor.city})` : ''}
                  {masterVendor.contact_name ? ` - ${masterVendor.contact_name}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Pick a saved vendor to prefill the form. Any new vendor you add here is also stored in the master list automatically.
          </p>
        </div>
      )}

      <div>
        <Label htmlFor="name">Vendor Name *</Label>
        <Input
          id="name"
          value={vendor.name}
          onChange={(e) => setVendor({ ...vendor, name: e.target.value })}
          required
          data-testid="vendor-name-input"
        />
      </div>

      <div>
        <Label htmlFor="category">Category *</Label>
        <Select value={categorySelectValue} onValueChange={updateCategory}>
          <SelectTrigger data-testid="vendor-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableCategories.map((category) => (
              <SelectItem key={category} value={category}>
                {formatCategoryLabel(category)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {categorySelectValue === 'other' && (
        <div>
          <Label htmlFor="custom_category">Custom Category *</Label>
          <Input
            id="custom_category"
            value={vendor.custom_category || selectedCustomCategory}
            onChange={(e) => {
              const customCategory = e.target.value;
              setVendor({
                ...vendor,
                custom_category: customCategory,
                category: customCategory.trim() || 'other'
              });
            }}
            placeholder="e.g. Security, Hospitality, Priest"
            required
            data-testid="vendor-custom-category-input"
          />
        </div>
      )}

      {requireItinerary && (
        <ItineraryCheckboxList
          items={itineraryItems}
          selectedIds={getSelectedItineraryIds(vendor)}
          onChange={(itineraryItemIds) => setVendor({
            ...vendor,
            itinerary_item_ids: itineraryItemIds,
            itinerary_item_id: itineraryItemIds[0] || '',
          })}
          idPrefix={isEdit ? 'edit-vendor-itinerary' : 'vendor-itinerary'}
          defaultSelectAll={!isEdit}
        />
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="contact_name">Contact Person</Label>
          <Input
            id="contact_name"
            value={vendor.contact_name}
            onChange={(e) => setVendor({ ...vendor, contact_name: e.target.value })}
            data-testid="vendor-contact-input"
          />
        </div>
        <div>
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            value={vendor.city}
            onChange={(e) => setVendor({ ...vendor, city: e.target.value })}
            placeholder="e.g. Mumbai"
            data-testid="vendor-city-input"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={vendor.phone}
            onChange={(e) => setVendor({ ...vendor, phone: e.target.value })}
            data-testid="vendor-phone-input"
          />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={vendor.email}
            onChange={(e) => setVendor({ ...vendor, email: e.target.value })}
            data-testid="vendor-email-input"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="cost_level">Cost</Label>
          <Select value={vendor.cost_level || 'med'} onValueChange={(value) => setVendor({ ...vendor, cost_level: value })}>
            <SelectTrigger data-testid="vendor-cost-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(tierConfig).map(([value, config]) => (
                <SelectItem key={value} value={value}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="quality_level">Quality</Label>
          <Select value={vendor.quality_level || 'med'} onValueChange={(value) => setVendor({ ...vendor, quality_level: value })}>
            <SelectTrigger data-testid="vendor-quality-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(tierConfig).map(([value, config]) => (
                <SelectItem key={value} value={value}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="notes">Other Remarks</Label>
        <Textarea
          id="notes"
          value={vendor.notes}
          onChange={(e) => setVendor({ ...vendor, notes: e.target.value })}
          rows={3}
          data-testid="vendor-notes-input"
        />
      </div>

      <DialogFooter>
        <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90" data-testid="save-vendor-btn">
          {submitLabel || `${isEdit ? 'Update' : 'Add'} Vendor`}
        </Button>
      </DialogFooter>
    </form>
  );
}

function VendorCard({
  vendor,
  canEdit,
  onEdit,
  onAddToEvent,
  canAddToEvent,
  onRemove,
  dataTestIdPrefix
}) {
  return (
    <Card className="shadow-sm" data-testid={`${dataTestIdPrefix}-${vendor.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="font-semibold">{vendor.name}</h3>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge className={getTierColor(vendor.cost_level)}>
                Cost: {getTierLabel(vendor.cost_level)}
              </Badge>
              <Badge className={getTierColor(vendor.quality_level)}>
                Quality: {getTierLabel(vendor.quality_level)}
              </Badge>
            </div>
          </div>
          {(canEdit || onRemove) && (
            <div className="flex items-center gap-2 shrink-0">
              {canEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => onEdit(vendor)}
                  data-testid={`${dataTestIdPrefix}-edit-btn-${vendor.id}`}
                >
                  <PencilSimple className="w-4 h-4" />
                </Button>
              )}
              {onRemove && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-destructive hover:bg-red-50 hover:text-destructive"
                  onClick={() => onRemove(vendor)}
                >
                  <Trash className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {vendor.email && (
            <a
              href={`mailto:${vendor.email}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-wedding-sage hover:bg-wedding-sage/10"
              aria-label={`Email ${vendor.name}`}
              title={vendor.email}
            >
              <EnvelopeSimple className="h-4 w-4" />
            </a>
          )}
        </div>

        {(vendor.contact_name || vendor.phone || vendor.city || vendor.notes) && (
          <CompactDetails label="Contact details" contentClassName="space-y-1 border-l-2 border-muted pl-3">
            {(vendor.contact_name || vendor.phone) && (
              <div className="flex items-center gap-1">
                {vendor.contact_name && <p className="font-medium text-foreground">{vendor.contact_name}</p>}
                {vendor.phone && (
                  <a
                    href={`tel:${vendor.phone}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full text-wedding-sage hover:bg-wedding-sage/10"
                    aria-label={`Call ${vendor.contact_name || vendor.name}`}
                    title={vendor.phone}
                  >
                    <Phone className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}
            {vendor.city && <p className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {vendor.city}</p>}
            {vendor.notes && <p>{vendor.notes}</p>}
          </CompactDetails>
        )}

        {onAddToEvent && canAddToEvent && (
          <div className="mt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onAddToEvent(vendor)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Current Event
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VendorGrid({
  vendors,
  canEdit,
  onEdit,
  onAddToEvent,
  canAddToEvent,
  onRemove,
  emptyMessage,
  dataTestIdPrefix
}) {
  const categories = defaultCategoryOrder.filter((category) => vendors.some((vendor) => vendor.category === category));
  const customCategories = Array.from(new Set(
    vendors
      .map((vendor) => vendor.category)
      .filter((category) => category && !defaultCategoryOrder.includes(category))
  ));
  const displayCategories = [...categories, ...customCategories];

  if (vendors.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Truck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {displayCategories.map((category) => {
        const categoryVendors = vendors.filter((vendor) => vendor.category === category);
        if (categoryVendors.length === 0) return null;
        const config = getCategoryAppearance(category);

        return (
          <Card key={category}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Badge className={config.color}>{config.label}</Badge>
                <span className="text-sm text-muted-foreground">({categoryVendors.length})</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryVendors.map((vendor) => (
                  <VendorCard
                    key={vendor.id}
                    vendor={vendor}
                    canEdit={canEdit}
                    onEdit={onEdit}
                    onAddToEvent={onAddToEvent}
                    canAddToEvent={canAddToEvent?.(vendor) ?? true}
                    onRemove={onRemove}
                    dataTestIdPrefix={dataTestIdPrefix}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ItineraryVendorGroups({
  vendors,
  itineraryItems,
  canEdit,
  onEdit,
  onRemove,
  dataTestIdPrefix,
  showGuestSides = true,
}) {
  if (vendors.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Truck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No vendors added to this event yet.</p>
        </CardContent>
      </Card>
    );
  }

  const itineraryIds = new Set(itineraryItems.map((item) => item.id));
  const groups = itineraryItems
    .map((item) => ({
      id: item.id,
      title: item.name,
      detail: getItineraryScheduleLabel(item) || 'Schedule not set',
      side: item.side || 'both',
      vendors: vendors.filter((vendor) => vendor.itinerary_item_id === item.id),
    }))
    .filter((group) => group.vendors.length > 0);
  const unassignedVendors = vendors.filter(
    (vendor) => !vendor.itinerary_item_id || !itineraryIds.has(vendor.itinerary_item_id)
  );
  if (unassignedVendors.length > 0) {
    groups.push({
      id: 'unassigned',
      title: 'Unassigned vendors',
      detail: 'Edit these vendors and select an itinerary event.',
      side: null,
      vendors: unassignedVendors,
    });
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.id} className="rounded-lg border bg-card p-4" data-testid={`itinerary-vendor-group-${group.id}`}>
          <div className="mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-serif text-xl font-semibold text-wedding-charcoal">{group.title}</h2>
              {showGuestSides && group.side && (
                <Badge
                  variant="outline"
                  className={`border px-2 py-0.5 text-[0.6875rem] font-medium ${getItinerarySideAppearance(group.side)}`}
                  data-testid={`itinerary-side-${group.id}`}
                >
                  {getItinerarySideLabel(group.side)}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{group.detail}</p>
          </div>
          <VendorGrid
            vendors={group.vendors}
            canEdit={canEdit}
            onEdit={onEdit}
            onRemove={onRemove}
            emptyMessage="No vendors assigned."
            dataTestIdPrefix={dataTestIdPrefix}
          />
        </section>
      ))}
    </div>
  );
}

export default function VendorManagementShared() {
  const { currentEvent, user } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const itineraryEnabled = eventTypeConfig.features.showItinerary;
  const navigate = useNavigate();
  const canManageVendors = ['event_manager', 'bride', 'groom', 'host'].includes(user?.role);
  const [masterVendors, setMasterVendors] = useState([]);
  const [eventVendors, setEventVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('event');
  const [masterCityFilter, setMasterCityFilter] = useState(ALL_CITIES_VALUE);

  const [showAddMasterDialog, setShowAddMasterDialog] = useState(false);
  const [showEditMasterDialog, setShowEditMasterDialog] = useState(false);
  const [showAddEventDialog, setShowAddEventDialog] = useState(false);
  const [showEditEventDialog, setShowEditEventDialog] = useState(false);
  const [showMissingItineraryDialog, setShowMissingItineraryDialog] = useState(false);
  const [pendingMasterVendor, setPendingMasterVendor] = useState(null);
  const [masterVendorItineraryIds, setMasterVendorItineraryIds] = useState([]);

  const [editingMasterVendor, setEditingMasterVendor] = useState(null);
  const [editingEventVendor, setEditingEventVendor] = useState(null);

  const [newMasterVendor, setNewMasterVendor] = useState(createVendorForm());
  const [newEventVendor, setNewEventVendor] = useState(createVendorForm());

  useEffect(() => {
    if (!currentEvent?.id) {
      setLoading(false);
      return;
    }

    const loadVendors = async () => {
      setLoading(true);
      await Promise.all([fetchMasterVendors(), fetchEventVendors()]);
      setLoading(false);
    };

    loadVendors();
  }, [currentEvent?.id]);

  const fetchMasterVendors = async () => {
    try {
      const response = await api.get('/vendor-master');
      setMasterVendors(response.data);
    } catch (error) {
      toast.error('Failed to fetch master vendor list');
    }
  };

  const fetchEventVendors = async () => {
    if (!currentEvent?.id) return;
    try {
      const response = await api.get(`/events/${currentEvent.id}/vendors`);
      setEventVendors(response.data);
    } catch (error) {
      toast.error('Failed to fetch event vendors');
    }
  };

  const availableCategories = useMemo(() => {
    const customCategories = Array.from(new Set(
      [...masterVendors, ...eventVendors]
        .map((vendor) => vendor.category)
        .filter((category) => category && !defaultCategoryOrder.includes(category))
    ));
    return [...defaultCategoryOrder, ...customCategories];
  }, [masterVendors, eventVendors]);

  const itineraryItems = useMemo(
    () => itineraryEnabled ? sortItineraryItems(currentEvent?.itinerary) : [],
    [currentEvent?.itinerary, itineraryEnabled]
  );
  const eventVendorRepresentatives = useMemo(() => {
    if (itineraryEnabled) return eventVendors;
    const vendorsByIdentity = new Map();
    eventVendors.forEach((vendor) => {
      const identity = vendor.master_vendor_id || vendor.id;
      if (!vendorsByIdentity.has(identity)) vendorsByIdentity.set(identity, vendor);
    });
    return Array.from(vendorsByIdentity.values());
  }, [eventVendors, itineraryEnabled]);
  const pendingMasterItineraryItems = useMemo(() => {
    if (!pendingMasterVendor) return itineraryItems;
    const assignedIds = new Set(
      eventVendors
        .filter((vendor) => vendor.master_vendor_id === pendingMasterVendor.id)
        .map((vendor) => vendor.itinerary_item_id)
        .filter(Boolean)
    );
    return itineraryItems.filter((item) => !assignedIds.has(item.id));
  }, [eventVendors, itineraryItems, pendingMasterVendor]);

  const masterCities = useMemo(() => {
    const citiesByKey = new Map();
    masterVendors.forEach((vendor) => {
      const city = normalizeText(vendor.city);
      if (city && !citiesByKey.has(city.toLocaleLowerCase())) {
        citiesByKey.set(city.toLocaleLowerCase(), city);
      }
    });
    return Array.from(citiesByKey.values()).sort((first, second) => first.localeCompare(second));
  }, [masterVendors]);

  const filteredMasterVendors = useMemo(() => {
    if (masterCityFilter === ALL_CITIES_VALUE) return masterVendors;
    const selectedCity = masterCityFilter.toLocaleLowerCase();
    return masterVendors.filter(
      (vendor) => normalizeText(vendor.city).toLocaleLowerCase() === selectedCity
    );
  }, [masterCityFilter, masterVendors]);

  useEffect(() => {
    if (
      masterCityFilter !== ALL_CITIES_VALUE
      && !masterCities.some((city) => city.toLocaleLowerCase() === masterCityFilter.toLocaleLowerCase())
    ) {
      setMasterCityFilter(ALL_CITIES_VALUE);
    }
  }, [masterCities, masterCityFilter]);

  const openAddMasterDialog = () => {
    setNewMasterVendor(createVendorForm());
    setShowAddMasterDialog(true);
  };

  const openEditMasterDialog = (vendor) => {
    setEditingMasterVendor(vendorToForm(vendor));
    setShowEditMasterDialog(true);
  };

  const openAddEventDialog = () => {
    if (itineraryEnabled && itineraryItems.length === 0) {
      setShowMissingItineraryDialog(true);
      return;
    }
    setNewEventVendor(createVendorForm());
    setShowAddEventDialog(true);
  };

  const openEditEventDialog = (vendor) => {
    const relatedAssignments = vendor.master_vendor_id
      ? eventVendors.filter((item) => item.master_vendor_id === vendor.master_vendor_id)
      : eventVendors.filter((item) => item.id === vendor.id);
    setEditingEventVendor(vendorToForm(vendor, relatedAssignments));
    setShowEditEventDialog(true);
  };

  const removeEventVendor = async (vendor) => {
    if (!currentEvent?.id) return;
    const relatedVendors = !itineraryEnabled && vendor.master_vendor_id
      ? eventVendors.filter((item) => item.master_vendor_id === vendor.master_vendor_id)
      : [vendor];
    if (!window.confirm(`Remove ${vendor.name} from this event?`)) return;

    try {
      await Promise.all(
        relatedVendors.map((item) => api.delete(`/events/${currentEvent.id}/vendors/${item.id}`))
      );
      toast.success('Vendor removed from event');
      fetchEventVendors();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to remove vendor from event'));
    }
  };

  const openMasterVendorAssignment = async (vendor) => {
    if (!currentEvent?.id) {
      toast.error('Please select an event first');
      return;
    }
    if (!itineraryEnabled) {
      if (eventVendors.some((eventVendor) => eventVendor.master_vendor_id === vendor.id)) {
        toast.info('This vendor is already added to the event');
        return;
      }
      try {
        await createVendorAssignments(currentEvent.id, {
          ...vendor,
          master_vendor_id: vendor.id,
        }, [null], vendor.id);
        toast.success(`${vendor.name} added to the event`);
        fetchMasterVendors();
        fetchEventVendors();
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to add vendor to current event'));
      }
      return;
    }
    if (itineraryItems.length === 0) {
      setShowMissingItineraryDialog(true);
      return;
    }
    const assignedIds = new Set(
      eventVendors
        .filter((eventVendor) => eventVendor.master_vendor_id === vendor.id)
        .map((eventVendor) => eventVendor.itinerary_item_id)
        .filter(Boolean)
    );
    const availableItems = itineraryItems.filter((item) => !assignedIds.has(item.id));
    if (availableItems.length === 0) {
      toast.info('This vendor is already assigned to every itinerary event');
      return;
    }
    setPendingMasterVendor(vendor);
    setMasterVendorItineraryIds(availableItems.length === 1 ? [availableItems[0].id] : []);
  };

  const addMasterVendorToCurrentEvent = async () => {
    if (!currentEvent?.id || !pendingMasterVendor) return;
    if (masterVendorItineraryIds.length === 0) {
      toast.error('Select at least one itinerary event');
      return;
    }

    try {
      await createVendorAssignments(currentEvent.id, {
        ...pendingMasterVendor,
        master_vendor_id: pendingMasterVendor.id,
      }, masterVendorItineraryIds, pendingMasterVendor.id);
      toast.success(`${pendingMasterVendor.name} assigned to ${masterVendorItineraryIds.length} itinerary ${masterVendorItineraryIds.length === 1 ? 'event' : 'events'}`);
      setPendingMasterVendor(null);
      setMasterVendorItineraryIds([]);
      fetchMasterVendors();
      fetchEventVendors();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to add vendor to current event'));
    }
  };

  const applyMasterVendorSelection = (selectedVendor) => {
    if (!selectedVendor) {
      setNewEventVendor((prev) => ({
        ...prev,
        master_vendor_id: '',
      }));
      return;
    }

    setNewEventVendor({
      ...vendorToForm(selectedVendor),
      master_vendor_id: selectedVendor.id,
      itinerary_item_id: newEventVendor.itinerary_item_id,
      itinerary_item_ids: getSelectedItineraryIds(newEventVendor),
    });
  };

  const handleAddMasterVendor = async (e) => {
    e.preventDefault();
    try {
      const payload = buildVendorPayload(newMasterVendor);
      if (!payload.name) {
        toast.error('Vendor name is required');
        return;
      }
      if (!payload.category) {
        toast.error('Category is required');
        return;
      }
      await api.post('/vendor-master', payload);
      toast.success('Vendor added to master list');
      setShowAddMasterDialog(false);
      setNewMasterVendor(createVendorForm());
      fetchMasterVendors();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to add master vendor'));
    }
  };

  const handleUpdateMasterVendor = async (e) => {
    e.preventDefault();
    if (!editingMasterVendor?.id) return;

    try {
      const payload = buildVendorPayload(editingMasterVendor);
      if (!payload.name) {
        toast.error('Vendor name is required');
        return;
      }
      if (!payload.category) {
        toast.error('Category is required');
        return;
      }
      await api.put(`/vendor-master/${editingMasterVendor.id}`, payload);
      toast.success('Master vendor updated');
      setShowEditMasterDialog(false);
      setEditingMasterVendor(null);
      fetchMasterVendors();
      fetchEventVendors();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update master vendor'));
    }
  };

  const handleAddEventVendor = async (e) => {
    e.preventDefault();
    if (!currentEvent?.id) return;

    try {
      const payload = buildVendorPayload(newEventVendor);
      if (!payload.name) {
        toast.error('Vendor name is required');
        return;
      }
      if (!payload.category) {
        toast.error('Category is required');
        return;
      }
      const selectedItineraryIds = itineraryEnabled ? getSelectedItineraryIds(newEventVendor) : [null];
      if (itineraryEnabled && selectedItineraryIds.length === 0) {
        toast.error('Select at least one itinerary event');
        return;
      }

      await createVendorAssignments(currentEvent.id, newEventVendor, selectedItineraryIds);
      toast.success(itineraryEnabled
        ? `Vendor assigned to ${selectedItineraryIds.length} itinerary ${selectedItineraryIds.length === 1 ? 'event' : 'events'} and saved to the master list`
        : 'Vendor added to the event and saved to the master list');
      setShowAddEventDialog(false);
      setNewEventVendor(createVendorForm());
      fetchMasterVendors();
      fetchEventVendors();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to add vendor'));
    }
  };

  const handleUpdateEventVendor = async (e) => {
    e.preventDefault();
    if (!currentEvent?.id || !editingEventVendor?.id) return;

    try {
      const payload = buildVendorPayload(editingEventVendor);
      if (!payload.name) {
        toast.error('Vendor name is required');
        return;
      }
      if (!payload.category) {
        toast.error('Category is required');
        return;
      }
      if (!itineraryEnabled) {
        const relatedAssignments = editingEventVendor.master_vendor_id
          ? eventVendors.filter((item) => item.master_vendor_id === editingEventVendor.master_vendor_id)
          : eventVendors.filter((item) => item.id === editingEventVendor.id);
        await Promise.all(relatedAssignments.map((item) => api.put(
          `/events/${currentEvent.id}/vendors/${item.id}`,
          { ...payload, itinerary_item_id: item.itinerary_item_id || null }
        )));
        toast.success('Vendor updated');
        setShowEditEventDialog(false);
        setEditingEventVendor(null);
        fetchMasterVendors();
        fetchEventVendors();
        return;
      }
      const selectedItineraryIds = getSelectedItineraryIds(editingEventVendor);
      if (selectedItineraryIds.length === 0) {
        toast.error('Select at least one itinerary event');
        return;
      }

      const relatedAssignments = editingEventVendor.master_vendor_id
        ? eventVendors.filter((item) => item.master_vendor_id === editingEventVendor.master_vendor_id)
        : eventVendors.filter((item) => item.id === editingEventVendor.id);
      const existingByItineraryId = new Map(
        relatedAssignments
          .filter((item) => item.itinerary_item_id)
          .map((item) => [item.itinerary_item_id, item])
      );
      const retainedAssignment = relatedAssignments.find(
        (item) => selectedItineraryIds.includes(item.itinerary_item_id)
      );
      const updateTarget = retainedAssignment
        || relatedAssignments.find((item) => item.id === editingEventVendor.id)
        || relatedAssignments[0];
      const updateTargetItineraryId = retainedAssignment?.itinerary_item_id || selectedItineraryIds[0];

      const updateResponse = await api.put(
        `/events/${currentEvent.id}/vendors/${updateTarget.id}`,
        { ...payload, itinerary_item_id: updateTargetItineraryId }
      );
      const newlySelectedIds = selectedItineraryIds.filter(
        (itemId) => itemId !== updateTargetItineraryId && !existingByItineraryId.has(itemId)
      );
      if (newlySelectedIds.length > 0) {
        await createVendorAssignments(
          currentEvent.id,
          editingEventVendor,
          newlySelectedIds,
          updateResponse.data?.master_vendor_id || editingEventVendor.master_vendor_id
        );
      }

      const assignmentsToRemove = relatedAssignments.filter(
        (item) => item.id !== updateTarget.id && !selectedItineraryIds.includes(item.itinerary_item_id)
      );
      await Promise.all(
        assignmentsToRemove.map((item) => api.delete(`/events/${currentEvent.id}/vendors/${item.id}`))
      );

      toast.success(`Vendor updated across ${selectedItineraryIds.length} itinerary ${selectedItineraryIds.length === 1 ? 'event' : 'events'}`);
      setShowEditEventDialog(false);
      setEditingEventVendor(null);
      fetchMasterVendors();
      fetchEventVendors();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update vendor'));
    }
  };

  const masterVendorCount = masterVendors.length;
  const eventVendorCount = new Set(
    eventVendors.map((vendor) => vendor.master_vendor_id || vendor.id)
  ).size;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <StickyActionHeader>
          <div>
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="vendors-title">
              Vendor Management
            </h1>
            <p className="text-muted-foreground">
              {masterVendorCount} master contacts | {eventVendorCount} vendors in this event
            </p>
          </div>

          {canManageVendors && (
            <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end" data-header-action-group>
              {/* <Button variant="outline" onClick={openAddMasterDialog} data-testid="add-master-vendor-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Master Vendor
              </Button> */}
              <Button
                className="bg-wedding-sage hover:bg-wedding-sage/90"
                onClick={openAddEventDialog}
                aria-label="Add event vendor"
                title="Add event vendor"
                data-header-action
                data-testid="add-vendor-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Event Vendor
              </Button>
            </div>
          )}
        </StickyActionHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="event" data-testid="event-vendors-tab">Current Event</TabsTrigger>
            <TabsTrigger value="master" data-testid="master-vendors-tab">Master List</TabsTrigger>
          </TabsList>

          <TabsContent value="master" className="space-y-6">
            <Card className="border-dashed border-wedding-sage/30 bg-wedding-sage/5">
              <CardContent className="flex flex-col gap-4 py-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-wedding-charcoal">Master vendor directory</p>
                    <InfoTip content="Add contacts here once, then reuse them across events." label="About the master vendor directory" />
                  </div>
                  {masterCityFilter !== ALL_CITIES_VALUE && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Showing {filteredMasterVendors.length} of {masterVendors.length} vendors
                    </p>
                  )}
                </div>
                <div className="w-full sm:w-56">
                  <Label htmlFor="master-city-filter">Filter by city</Label>
                  <Select value={masterCityFilter} onValueChange={setMasterCityFilter}>
                    <SelectTrigger id="master-city-filter" data-testid="master-city-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_CITIES_VALUE}>All cities</SelectItem>
                      {masterCities.map((city) => (
                        <SelectItem key={city.toLocaleLowerCase()} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">Loading master vendors...</CardContent>
              </Card>
            ) : (
            <VendorGrid
              vendors={filteredMasterVendors}
              canEdit={canManageVendors}
              onEdit={openEditMasterDialog}
              onAddToEvent={openMasterVendorAssignment}
              emptyMessage={masterCityFilter === ALL_CITIES_VALUE
                ? 'No master vendors yet. Add your first contact to build the shared list.'
                : `No master vendors found in ${masterCityFilter}.`}
              dataTestIdPrefix="master-vendor"
            />
            )}
          </TabsContent>

          <TabsContent value="event" className="space-y-6">
            <Card className="border-dashed border-wedding-sage/30 bg-wedding-sage/5">
              <CardContent className="py-4">
                <p className="text-sm text-muted-foreground">
                  {itineraryEnabled
                    ? 'Vendors are grouped by itinerary event and their contact details stay synced with the master list.'
                    : 'Event vendors and their contact details stay synced with the master list.'}
                </p>
              </CardContent>
            </Card>

            {loading ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">Loading event vendors...</CardContent>
              </Card>
            ) : itineraryEnabled && itineraryItems.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <h2 className="font-serif text-xl font-semibold">Add an itinerary event first</h2>
                  <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
                    Vendors are assigned to itinerary events so everyone knows which function they are supporting.
                  </p>
                  {canManageVendors && (
                    <Button className="mt-4 bg-wedding-sage hover:bg-wedding-sage/90" onClick={() => navigate('/dashboard/itinerary')}>
                      Go to Itinerary
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : itineraryEnabled ? (
              <ItineraryVendorGroups
                vendors={eventVendors}
                itineraryItems={itineraryItems}
                canEdit={canManageVendors}
                onEdit={openEditEventDialog}
                onRemove={removeEventVendor}
                dataTestIdPrefix="event-vendor"
                showGuestSides={eventTypeConfig.features.showGuestSides}
              />
            ) : (
              <VendorGrid
                vendors={eventVendorRepresentatives}
                canEdit={canManageVendors}
                onEdit={openEditEventDialog}
                onRemove={removeEventVendor}
                emptyMessage="No vendors have been added to this event yet."
                dataTestIdPrefix="event-vendor"
              />
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={showAddMasterDialog} onOpenChange={setShowAddMasterDialog}>
          <DialogContent className="max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Master Vendor</DialogTitle>
              <DialogDescription>Add a reusable vendor contact to your shared directory.</DialogDescription>
            </DialogHeader>
            <VendorForm
              vendor={newMasterVendor}
              setVendor={setNewMasterVendor}
              onSubmit={handleAddMasterVendor}
              isEdit={false}
              availableCategories={availableCategories}
              submitLabel="Save Master Vendor"
            />
          </DialogContent>
        </Dialog>

        <Dialog open={showEditMasterDialog} onOpenChange={(open) => !open && setShowEditMasterDialog(false)}>
          <DialogContent className="max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Master Vendor</DialogTitle>
              <DialogDescription>Update the shared contact details for this vendor.</DialogDescription>
            </DialogHeader>
            {editingMasterVendor && (
              <VendorForm
                vendor={editingMasterVendor}
                setVendor={setEditingMasterVendor}
                onSubmit={handleUpdateMasterVendor}
                isEdit={true}
                availableCategories={availableCategories}
                submitLabel="Update Master Vendor"
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showAddEventDialog} onOpenChange={setShowAddEventDialog}>
          <DialogContent className="max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Event Vendor</DialogTitle>
              <DialogDescription>Select a saved vendor or enter new details for this event.</DialogDescription>
            </DialogHeader>
            <VendorForm
              vendor={newEventVendor}
              setVendor={setNewEventVendor}
              onSubmit={handleAddEventVendor}
              isEdit={false}
              availableCategories={availableCategories}
              masterVendors={masterVendors}
              showMasterPicker
              onMasterVendorSelect={applyMasterVendorSelection}
              itineraryItems={itineraryItems}
              requireItinerary={itineraryEnabled}
              submitLabel="Save Event Vendor"
            />
          </DialogContent>
        </Dialog>

        <Dialog open={showEditEventDialog} onOpenChange={(open) => !open && setShowEditEventDialog(false)}>
          <DialogContent className="max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Event Vendor</DialogTitle>
              <DialogDescription>Update the event-specific contact and keep the master record in sync.</DialogDescription>
            </DialogHeader>
            {editingEventVendor && (
              <VendorForm
                vendor={editingEventVendor}
                setVendor={setEditingEventVendor}
                onSubmit={handleUpdateEventVendor}
                isEdit={true}
                availableCategories={availableCategories}
                itineraryItems={itineraryItems}
                requireItinerary={itineraryEnabled}
                submitLabel="Update Event Vendor"
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={Boolean(pendingMasterVendor)}
          onOpenChange={(open) => {
            if (!open) {
              setPendingMasterVendor(null);
              setMasterVendorItineraryIds([]);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add {pendingMasterVendor?.name} to Current Event</DialogTitle>
              <DialogDescription>Select every itinerary event this vendor will support.</DialogDescription>
            </DialogHeader>
            <ItineraryCheckboxList
              items={pendingMasterItineraryItems}
              selectedIds={masterVendorItineraryIds}
              onChange={setMasterVendorItineraryIds}
              idPrefix="master-vendor-itinerary"
            />
            <DialogFooter>
              <Button
                type="button"
                disabled={masterVendorItineraryIds.length === 0}
                className="bg-wedding-sage hover:bg-wedding-sage/90"
                onClick={addMasterVendorToCurrentEvent}
              >
                Add to {masterVendorItineraryIds.length || 0} {masterVendorItineraryIds.length === 1 ? 'Event' : 'Events'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showMissingItineraryDialog} onOpenChange={setShowMissingItineraryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add an Itinerary Event First</DialogTitle>
              <DialogDescription>
                Every event vendor must be tagged to an itinerary event. Add the function first, then return to assign the vendor.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMissingItineraryDialog(false)}>Cancel</Button>
              <Button
                className="bg-wedding-sage hover:bg-wedding-sage/90"
                onClick={() => {
                  setShowMissingItineraryDialog(false);
                  navigate('/dashboard/itinerary');
                }}
              >
                Go to Itinerary
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
